// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArchetypeSpecificStepRunSummaryPanelProps, StepRunSummaryCard } from '.';
import { ReleaseStepRolloutData } from '@/types';
import {
    getAutomationIdByType,
    getIsAppleAppStore,
    isPhasedReleasePaused,
    tryDeterminePhasedReleaseDaysToPercentageSchedule,
} from '@/helpers';
import { AutomationAutoStagedRollout, AutomationType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAutomationById } from '@/selectors/selectors';
import {
    PhasedReleaseProgressSection,
    AdoptionRateSection,
} from '@/components/workflowOverview/widgets/WorkflowRunPhasedReleaseWidget';

export const RolloutStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({ stepRun, appId }) => {
    const rolloutData: ReleaseStepRolloutData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const phasedRelease = rolloutData.phasedRelease;

    const automationRuns = stepRun.automationRuns;
    const autoStagedRolloutAutomationId = getAutomationIdByType(automationRuns, AutomationType.AutoStagedRollout);

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, appId, autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;
    const hasPhasedReleaseState = !!phasedRelease;
    const isAppleAppStore = getIsAppleAppStore(rolloutData.appStoreType);
    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const daysToPercent = tryDeterminePhasedReleaseDaysToPercentageSchedule(
        hasPhasedReleaseState,
        rolloutData.appStoreType,
        autoStagedRolloutAutomation,
    );

    const wasAccelerated =
        phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout;
    const wasHalted = isPhasedReleasePaused(phasedRelease?.state);

    const releaseAdoptions = rolloutData.releaseAdoptions ?? [];
    const firstAdoption = releaseAdoptions.length > 0 ? releaseAdoptions[0] : null;
    const adoptionRate = firstAdoption?.adoptionRate ?? 0;

    const destinationAdoptions: [(typeof stepRun.destinations)[0], number][] =
        adoptionRate > 0 && stepRun.destinations.length > 0 ? [[stepRun.destinations[0], adoptionRate]] : [];

    if (!phasedRelease) {
        return null;
    }

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <StepRunSummaryCard>
                <AdoptionRateSection
                    destinationAdoptions={destinationAdoptions}
                    stabIntegrationId={rolloutData.stabilityMonitoringIntegrationType}
                    showExpandButton={false}
                    showTitle={true}
                />
            </StepRunSummaryCard>

            <StepRunSummaryCard>
                <PhasedReleaseProgressSection
                    destinations={stepRun.destinations}
                    percentage={phasedRelease.phasedReleasePercent}
                    dayNumber={phasedRelease.dayNumber}
                    daysToPercent={daysToPercent}
                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                    isAccelerated={wasAccelerated}
                    isRunwayAcceleratedRollout={phasedRelease.isRunwayAcceleratedRollout}
                    isHalted={wasHalted}
                    isRunwayHalted={rolloutData.arePhasedReleaseAutomationsPaused}
                />
            </StepRunSummaryCard>
        </div>
    );
};
